//Dependencies
import React from 'react';
import { Link, useHistory } from 'react-router-dom';
import { FiUsers, FiUserPlus, FiActivity, FiBookOpen, FiArrowRight, FiClock } from 'react-icons/fi';
import UpdatePassword from '../Components/AdminDashboard/UpdatePassword';
import { useUserData } from '../Components/Hooks/useUserData';
import { useCachedQuery } from '../Components/Hooks/useCachedQuery';
import { PageHeader, Kpi, Skeleton, Notice } from '../Components/UI';
import BarChart from '../Components/UI/BarChart';
import { fetchWorksheetStats, fetchWorksheetUsers } from '../Utils/WorksheetUsersAPI';
import { fetchReaderStats, fetchActivitySummary } from '../Utils/ReaderUsersAPI';
import { formatMinutes, formatNumber, initialsOf, timeAgo } from '../Utils/Format';
import { PlanBadge } from './WorksheetsUsers';

const greeting = () => {
  const hour = new Date().getHours();
  return hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
};

const RECENT_QUERY = { page: 1, limit: 6, search: '', activity: 'all' as const, plan: 'all', from: '', to: '', sort: 'created', dir: 'desc' as const };

const AdminDashboard: React.FC = () => {
  const { userData } = useUserData();
  const history = useHistory();
  const stats = useCachedQuery('ws:stats', (signal) => fetchWorksheetStats(signal), 60000);
  const recent = useCachedQuery('overview:recent', (signal) => fetchWorksheetUsers(RECENT_QUERY, signal), 60000);
  const readerStats = useCachedQuery('rd:stats', (signal) => fetchReaderStats(signal), 60000);
  const reading = useCachedQuery('overview:reading7', (signal) => fetchActivitySummary(7, signal), 60000);

  const firstName = ((userData.currentUser.full_name as string) || '').split(' ')[0];
  const s = stats.data;
  const a = reading.data;
  const r = readerStats.data;
  const recentUsers = recent.data ? recent.data.users : [];

  return (
    <div className="stack">
      <PageHeader
        title={firstName ? `${greeting()}, ${firstName}` : 'Overview'}
        description="A snapshot of worksheet and No Fuss Readers sign ups and reading."
        actions={
          <Link className="btn btn--coral" to="/admin/readingPortalUsers">
            <FiBookOpen /> Reading users
          </Link>
        }
      />

      <div className="grid grid--kpi">
        <Kpi
          label="Worksheet users"
          onClick={() => history.push('/admin/worksheetsUsers')}
          icon={<FiUsers />}
          tone="green"
          value={formatNumber(s && s.total)}
          loading={stats.loading && !s}
          sub={s && s.newLast7 !== undefined ? `+${formatNumber(s.newLast7)} this week` : `${formatNumber(s && s.withActivity)} downloaded a worksheet`}
        />
        <Kpi
          label="Active in 30 days"
          onClick={() => history.push('/admin/worksheetsUsers?activity=with&sort=activity')}
          icon={<FiActivity />}
          tone="amber"
          value={s && s.activeLast30 !== undefined ? formatNumber(s.activeLast30) : formatNumber(s && s.withActivity)}
          loading={stats.loading && !s}
          sub={s && s.activeLast30 !== undefined ? 'Downloaded at least one worksheet' : 'All time, with downloads'}
        />
        <Kpi
          label="Reading parents"
          onClick={() => history.push('/admin/readingPortalUsers')}
          icon={<FiBookOpen />}
          tone="coral"
          value={formatNumber(r && r.parents)}
          loading={readerStats.loading && !r}
          sub={r ? `+${formatNumber(r.newLast7)} this week` : ' '}
        />
        <Kpi
          label="Read this week"
          onClick={() => history.push('/admin/readingPortalUsers?read=7&type=all&site=all&days=7')}
          icon={<FiClock />}
          value={a ? formatMinutes(a.minutes) : reading.loading ? '' : '0 min'}
          loading={reading.loading && !a}
          sub={a ? `${formatNumber(a.activeReaders)} readers, ${formatNumber(a.booksFinished)} books finished` : 'Time tracking started Oct 31, 2026'}
        />
      </div>

      <div className="grid grid--2">
        <div className="panel">
          <div className="panel__head">
            <div>
              <h2>Worksheet sign ups</h2>
              <p>New accounts per day, last 30 days</p>
            </div>
            {s && s.newLast30 !== undefined && (
              <div style={{ textAlign: 'right' }}>
                <div className="kpi__value" style={{ fontSize: '2.4rem' }}>
                  {formatNumber(s.newLast30)}
                </div>
                <div className="kpi__sub">in 30 days</div>
              </div>
            )}
          </div>
          {s && s.signupsByDay ? (
            <BarChart data={s.signupsByDay} />
          ) : stats.loading ? (
            <Skeleton width="100%" height="16rem" block />
          ) : (
            <p className="muted" style={{ padding: '4rem 0', textAlign: 'center' }}>
              Daily sign ups appear here once the server update is deployed.
            </p>
          )}
        </div>

        <div className="panel">
          <div className="panel__head">
            <div>
              <h2>Newest users</h2>
              <p>Latest worksheet accounts</p>
            </div>
            <Link to="/admin/worksheetsUsers" className="btn btn--ghost btn--sm">
              View all <FiArrowRight />
            </Link>
          </div>
          {recent.loading && !recent.data
            ? [0, 1, 2, 3, 4].map((i) => (
                <div key={i} className="cell-user" style={{ display: 'flex', gap: '1.2rem', padding: '0.8rem 0' }}>
                  <Skeleton circle width="3.4rem" height="3.4rem" />
                  <div style={{ flex: 1 }}>
                    <Skeleton width="60%" block />
                  </div>
                </div>
              ))
            : recentUsers.map((user) => (
                <Link
                  key={user._id}
                  to={`/admin/worksheetsUsers?user=${user._id}`}
                  style={{ display: 'flex', alignItems: 'center', gap: '1.2rem', padding: '0.8rem 0', color: 'inherit', borderBottom: '1px solid #eef1f6' }}
                >
                  <span className="avatar" style={{ width: '3.4rem', height: '3.4rem', fontSize: '1.2rem', background: '#eef3fc', color: '#133a88' }}>
                    {initialsOf(user.name, user.email)}
                  </span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {user.name && user.name.trim() ? user.name : user.email}
                    </div>
                    <div className="muted" style={{ fontSize: '1.25rem' }}>
                      {recent.data && recent.data.legacy ? `Downloaded ${timeAgo(user.lastActivity)}` : `Joined ${timeAgo(user.createdAt)}`}
                    </div>
                  </div>
                  <PlanBadge plan={user.plan} />
                </Link>
              ))}
        </div>
      </div>

      <div className="grid grid--2">
        <div className="panel">
          <div className="panel__head">
            <div>
              <h2>No Fuss Readers</h2>
              <p>Parent accounts and reading activity</p>
            </div>
            <Link to="/admin/readingPortalUsers" className="btn btn--ghost btn--sm">
              Reading users <FiArrowRight />
            </Link>
          </div>
          {readerStats.error ? (
            <Notice tone="error">Could not load Readers stats: {readerStats.error.message}</Notice>
          ) : (
            <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(14rem, 1fr))', gap: '1.6rem' }}>
              {[
                { label: 'Parents', value: r && r.parents, icon: <FiUsers /> },
                { label: 'New this week', value: r && r.newLast7, icon: <FiUserPlus /> },
                { label: 'Opened a story', value: r && r.withActivity, icon: <FiBookOpen /> },
                { label: 'Paying (Stripe)', value: r && (r.payingStripe ?? r.paid), icon: <FiActivity /> },
              ].map((item) => (
                <div key={item.label}>
                  <div className="kpi__sub" style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                    {item.icon} {item.label}
                  </div>
                  <div className="kpi__value" style={{ fontSize: '2.4rem', marginTop: '0.4rem' }}>
                    {r ? formatNumber(item.value) : <Skeleton width="7rem" height="2.4rem" />}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="panel">
          <div className="panel__head">
            <div>
              <h2>Quick actions</h2>
              <p>Common tasks</p>
            </div>
          </div>
          <div style={{ display: 'grid', gap: '0.8rem' }}>
            <Link className="btn btn--secondary" style={{ justifyContent: 'flex-start' }} to="/admin/worksheetsUsers">
              <FiUsers /> Find a worksheet user
            </Link>
            <Link className="btn btn--secondary" style={{ justifyContent: 'flex-start' }} to="/admin/readingPortalUsers">
              <FiBookOpen /> Find a reading user
            </Link>
            <Link className="btn btn--secondary" style={{ justifyContent: 'flex-start' }} to="/admin/readingPortalUsers?days=7">
              <FiClock /> Reading activity this week
            </Link>
          </div>
        </div>
      </div>

      <div className="panel" style={{ maxWidth: '64rem' }}>
        <div className="panel__head">
          <div>
            <h2>Your account</h2>
            <p>Change your admin dashboard password</p>
          </div>
        </div>
        <UpdatePassword />
      </div>
    </div>
  );
};

export default AdminDashboard;
